import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { GameLayout } from './GameLayout';
import { categoryLabel, difficultyLabel, useQuizzes } from '../data/quizzes';
import { QUIZ_TEXT, useQuizLang } from '../lib/quizLang';

export function QuizPlay() {
  const { quizId } = useParams();
  const [lang] = useQuizLang();
  const t = QUIZ_TEXT[lang];
  const { quizzes, ready } = useQuizzes(lang);
  const quiz = useMemo(() => quizzes.find((q) => q.id === quizId), [quizzes, quizId]);

  const [idx, setIdx] = useState(0);
  const [chosen, setChosen] = useState<number | null>(null);
  const [answers, setAnswers] = useState<boolean[]>([]);
  const [done, setDone] = useState(false);

  const nextQuiz = useMemo(() => {
    if (!quiz) return null;
    const i = quizzes.findIndex((q) => q.id === quiz.id);
    if (i < 0) return null;
    return quizzes[(i + 1) % quizzes.length] ?? null;
  }, [quiz, quizzes]);

  // Смена языка (или догрузка её чанка) на середине прохождения — начинаем квиз заново.
  useEffect(() => {
    setIdx(0);
    setChosen(null);
    setAnswers([]);
    setDone(false);
  }, [lang, quizId, ready]);

  if (!quiz) {
    return (
      <GameLayout>
        <span className="label">{t.notFoundLabel}</span>
        <h1 style={{ fontFamily: 'Oswald', fontWeight: 600, fontSize: 72, margin: '12px 0 20px', letterSpacing: '-.015em' }}>
          {t.notFound}
        </h1>
        <p style={{ maxWidth: 480, color: 'var(--ink-dim)', fontSize: 17, lineHeight: 1.55, margin: '0 0 32px' }}>
          {t.notFoundText(quizzes.length)}
        </p>
        <Link to="/quizzes" className="btn-primary lg">
          {t.allQuizzes}
        </Link>
      </GameLayout>
    );
  }

  const question = quiz.questions[idx];
  const score = answers.filter(Boolean).length;
  const total = quiz.questions.length;
  const pct = total ? Math.round((score / total) * 100) : 0;
  const verdict = t.verdicts.find((v) => pct >= v.min) ?? t.verdicts[t.verdicts.length - 1];
  const bestKey = `quiz-best:${quiz.id}`;

  const choose = (i: number) => {
    if (chosen !== null) return;
    setChosen(i);
    setAnswers((a) => [...a, i === question.correct]);
  };

  const next = () => {
    if (idx + 1 >= total) {
      try {
        const prev = Number(localStorage.getItem(bestKey) ?? 0);
        if (pct > prev) localStorage.setItem(bestKey, String(pct));
      } catch {
        /* localStorage недоступен — просто идём дальше */
      }
      setDone(true);
    } else {
      setIdx(idx + 1);
      setChosen(null);
    }
  };

  const restart = () => {
    setIdx(0);
    setChosen(null);
    setAnswers([]);
    setDone(false);
  };

  let best: number | null = null;
  try {
    best = Number(localStorage.getItem(bestKey) ?? -1);
    if (best < 0) best = null;
  } catch {
    best = null;
  }

  return (
    <GameLayout>
      <QuizPlayStyles />
      <div className="qp-topline">
        <Link to="/quizzes" className="label qp-back">
          {t.back}
        </Link>
      </div>

      <div className="qp-head">
        <span className="qp-emoji">{quiz.emoji}</span>
        <div>
          <h1 className="qp-title">{quiz.title}</h1>
          <div className="qp-sub">
            {categoryLabel(quiz.category, lang)} · {difficultyLabel(quiz.difficulty, lang)}
            {best !== null ? ` · ${t.record(best)}` : ''}
          </div>
        </div>
        <div className="qp-score">
          <span className="qp-score-n">{score}</span>
          <span className="qp-score-d">/ {total}</span>
        </div>
      </div>

      {done ? (
        <div className="qp-result">
          <span className="label">{t.resultLabel}</span>
          <div className="qp-pct">{pct}%</div>
          <h2 className="qp-verdict">{verdict.title}</h2>
          <p className="qp-verdict-text">{verdict.text}</p>
          <p className="qp-line">
            {t.correctLine(score, total)}
            {best !== null ? ` · ${t.bestLine(best)}` : ''}
          </p>
          <div className="qp-actions">
            <button className="btn-primary lg" onClick={restart}>
              {t.again}
            </button>
            {nextQuiz && (
              <Link to={`/quiz/${nextQuiz.id}`} className="btn-ghost lg" onClick={restart}>
                {t.nextQuiz}: {nextQuiz.title} →
              </Link>
            )}
          </div>
        </div>
      ) : (
        <div className="qp-game">
          <div className="qp-progress">
            <span className="mono qp-progress-label">{t.question(idx + 1, total)}</span>
            <div className="qp-progress-track">
              <div className="qp-progress-fill" style={{ width: `${((idx + (chosen !== null ? 1 : 0)) / total) * 100}%` }} />
            </div>
          </div>

          <h2 className="qp-question">{question.q}</h2>

          <div className="qp-options">
            {question.options.map((opt, i) => {
              let state = '';
              if (chosen !== null) {
                if (i === question.correct) state = ' ok';
                else if (i === chosen) state = ' bad';
                else state = ' dim';
              }
              return (
                <button key={i} className={`qp-opt${state}`} onClick={() => choose(i)} disabled={chosen !== null}>
                  <span className="qp-opt-letter">{String.fromCharCode(65 + i)}</span>
                  <span className="qp-opt-text">{opt}</span>
                </button>
              );
            })}
          </div>

          {chosen !== null && (
            <div className="qp-after">
              {question.fact ? <div className="qp-fact">{question.fact}</div> : null}
              <button className="btn-primary lg qp-next" onClick={next}>
                {idx + 1 >= total ? t.toResults : t.next}
              </button>
            </div>
          )}
        </div>
      )}
    </GameLayout>
  );
}

function QuizPlayStyles() {
  return (
    <style>{`
      .qp-topline { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 20px; }
      .qp-back { text-decoration: none; display: inline-block; }

      .qp-head { display: flex; align-items: center; gap: 22px; margin-bottom: 40px; }
      .qp-emoji { font-size: 54px; line-height: 1; }
      .qp-title { font-family: 'Oswald', sans-serif; font-weight: 600; font-size: 56px; line-height: 1; letter-spacing: -.01em; margin: 0 0 8px; }
      .qp-sub { font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: .14em; text-transform: uppercase; color: var(--ink-dim); }
      .qp-score { margin-left: auto; text-align: right; }
      .qp-score-n { font-family: 'Oswald', sans-serif; font-weight: 600; font-size: 44px; color: var(--accent); }
      .qp-score-d { font-family: 'JetBrains Mono', monospace; font-size: 14px; color: var(--ink-faint); }

      .qp-progress { display: flex; align-items: center; gap: 18px; margin-bottom: 36px; }
      .qp-progress-label { font-size: 11px; letter-spacing: .14em; text-transform: uppercase; color: var(--ink-faint); white-space: nowrap; }
      .qp-progress-track { flex: 1; height: 4px; background: var(--line); border-radius: 2px; overflow: hidden; }
      .qp-progress-fill { height: 100%; background: var(--accent); transition: width .4s cubic-bezier(.2,.8,.2,1); }

      .qp-question { font-family: 'Oswald', sans-serif; font-weight: 600; font-size: 42px; line-height: 1.12; letter-spacing: -.005em; margin: 0 0 32px; max-width: 900px; }

      .qp-options { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; max-width: 900px; }
      .qp-opt { display: flex; align-items: center; gap: 16px; text-align: left; padding: 20px 22px; background: var(--bg2);
        border: 1px solid var(--line); border-radius: var(--radius-card); color: var(--ink); cursor: pointer;
        font-size: 16px; line-height: 1.4; transition: border-color .15s, background .15s, opacity .15s; }
      .qp-opt:hover:not(:disabled) { border-color: var(--accent); }
      .qp-opt:disabled { cursor: default; }
      .qp-opt-letter { font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: .1em; color: var(--ink-faint);
        border: 1px solid var(--line-strong); border-radius: 3px; padding: 4px 7px; flex-shrink: 0; }
      .qp-opt.ok { border-color: var(--accent); background: color-mix(in srgb, var(--accent) 14%, var(--bg2)); }
      .qp-opt.ok .qp-opt-letter { background: var(--accent); color: var(--accent-ink); border-color: var(--accent); }
      .qp-opt.bad { border-color: rgba(224, 106, 106, .55); }
      .qp-opt.bad .qp-opt-letter { background: #e06a6a; color: #fff; border-color: #e06a6a; }
      .qp-opt.dim { opacity: .45; }

      .qp-after { margin-top: 28px; max-width: 900px; }
      .qp-fact { font-size: 14px; line-height: 1.55; color: var(--ink-dim); border-left: 2px solid var(--accent); padding: 4px 0 4px 16px; margin-bottom: 24px; }

      .qp-result { max-width: 720px; padding: 48px 0; }
      .qp-pct { font-family: 'Oswald', sans-serif; font-weight: 600; font-size: 140px; line-height: 1; letter-spacing: -.02em; margin: 16px 0 8px; }
      .qp-verdict { font-family: 'Oswald', sans-serif; font-weight: 600; font-size: 40px; line-height: 1.05; margin: 0 0 14px; }
      .qp-verdict-text { font-size: 17px; line-height: 1.55; color: var(--ink-dim); margin: 0 0 12px; }
      .qp-line { font-family: 'JetBrains Mono', monospace; font-size: 12px; letter-spacing: .1em; text-transform: uppercase; color: var(--ink-faint); margin: 0 0 36px; }
      .qp-actions { display: flex; gap: 12px; flex-wrap: wrap; }

      @media (max-width: 760px) {
        .qp-title { font-size: 38px; }
        .qp-emoji { font-size: 40px; }
        .qp-question { font-size: 30px; }
        .qp-options { grid-template-columns: 1fr; }
        .qp-pct { font-size: 96px; }
        .qp-verdict { font-size: 30px; }
      }
    `}</style>
  );
}
